import { useMemo, useState } from 'react';
import type { ProviderQuote } from '../../../features/catalog/model';
import type { SortState } from '../../../components/SortableTable';
import { orderProviders, servedShares, type SortKey } from '../ProvidersPanel';
import { providerKey } from '../ProviderRow';
import { DrawRow, type RowFill } from './DrawRow';
import { PlayCaption } from './PlayCaption';
import { playScript, type CallBeat } from './playScript';
import type { RowTone } from './RowParts';
import { usePlayOnce } from './usePlayOnce';
import { m } from '../../../paraglide/messages';

type OrderKey = SortKey | 'order';

const GRID =
  'grid grid-cols-[28px_40px_minmax(0,1fr)_auto] items-center gap-x-3 sm:grid-cols-[28px_40px_minmax(0,1fr)_auto_40px] lg:grid-cols-[28px_40px_minmax(0,1fr)_minmax(0,1fr)_88px_72px_136px_40px]';

interface HeadProps {
  label: string;
  k: OrderKey;
  sort: SortState<OrderKey>;
  onSort: (key: OrderKey) => void;
  className?: string;
}

const Head = ({ label, k, sort, onSort, className = '' }: HeadProps) => {
  const active = sort.key === k;
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      className={`mono flex min-h-10 items-center gap-1 whitespace-nowrap text-12 transition-colors hover:text-text ${
        active ? 'text-text' : 'text-text-3'
      } ${className}`}
    >
      {label}
      {k === 'order' ? null : (
        <span className={`text-9 ${active ? 'opacity-100' : 'opacity-0'}`}>
          {active && sort.dir === 'desc' ? '▼' : '▲'}
        </span>
      )}
    </button>
  );
};

const FAILED_BEATS: CallBeat[] = ['failed', 'handoff', 'answered'];
const DRAWN_BEATS: CallBeat[] = ['handoff', 'answered', 'settled'];

/** Row 1's and row 2's part in the example call; every other row stays at rest. */
const toneFor = (position: number | null, call: CallBeat): RowTone => {
  if (position === 0) return call === 'calling' ? 'calling' : FAILED_BEATS.includes(call) ? 'failed' : 'rest';
  if (position === 1 && call === 'answered') return 'answered';
  return 'rest';
};

const fillFor = (position: number | null): RowFill =>
  position === 0 ? 'lower' : position === 1 ? 'upper' : 'none';

export interface DrawTableProps {
  /** in the order calls are tried in, as the gateway authors them */
  providers: ProviderQuote[];
  notServing: ProviderQuote[];
}

/** The call-order table, drawn once when it scrolls into view: the rows arrive
 *  top down along the rail, then one example call fails on #1 and is handed
 *  down the rail to #2, and the table rests with a caption and Replay. */
export const DrawTable = ({ providers, notServing }: DrawTableProps) => {
  const [sort, setSort] = useState<SortState<OrderKey>>({ key: 'order', dir: 'asc' });
  const onSort = (key: OrderKey) =>
    setSort((previous) =>
      key === 'order'
        ? { key, dir: 'asc' }
        : previous.key === key
          ? { key, dir: previous.dir === 'asc' ? 'desc' : 'asc' }
          : { key, dir: key === 'uptime' || key === 'requests' ? 'desc' : 'asc' },
    );
  const inOrder = sort.key === 'order';
  const script = useMemo(() => playScript(providers.length + notServing.length), [providers.length, notServing.length]);
  const durations = useMemo(() => script.map((beat) => beat.ms), [script]);
  const play = usePlayOnce<HTMLDivElement>(durations, inOrder);
  // Sorting away from call order shows the plain table; the play waits for the order to return.
  const beat = play.index === null || !inOrder ? null : script[play.index];

  const sorted = (list: ProviderQuote[]) =>
    sort.key === 'order' ? list : orderProviders(list, { key: sort.key, dir: sort.dir });
  const rows = [...sorted(providers), ...sorted(notServing)];
  const all = [...providers, ...notServing];
  const shares = new Map(servedShares(all).map((share, index) => [providerKey(all[index]), share]));
  const position = new Map(providers.map((provider, index) => [providerKey(provider), index]));
  const lastKey = providerKey(providers[providers.length - 1]);

  return (
    <div ref={play.ref}>
      <div className="rounded-12 border border-rule bg-surface">
        <div className={`${GRID} rounded-t-12 border-b border-rule bg-wash px-4 sm:px-5`}>
          <Head label="#" k="order" sort={sort} onSort={onSort} className="justify-center" />
          <span className="mono col-span-2 whitespace-nowrap text-12 text-text-3">
            {m.endpoint_draw_provider()}
            {inOrder ? <span className="hidden sm:inline">{m.endpoint_draw_in_order()}</span> : null}
          </span>
          <Head label={m.endpoint_share_column()} k="requests" sort={sort} onSort={onSort} className="hidden lg:flex" />
          <Head label={m.endpoint_uptime_column()} k="uptime" sort={sort} onSort={onSort} className="hidden justify-end lg:flex" />
          <Head label="p50" k="latency" sort={sort} onSort={onSort} className="hidden justify-end lg:flex" />
          <Head label={m.endpoint_cost_column()} k="price" sort={sort} onSort={onSort} className="justify-end" />
          <span className="hidden sm:block" />
        </div>
        {rows.map((provider, index) => {
          const key = providerKey(provider);
          const at = position.get(key) ?? null;
          const call = beat?.call ?? 'none';
          return (
            <DrawRow
              key={key}
              provider={provider}
              grid={GRID}
              position={at}
              share={shares.get(key) ?? 0}
              rail={inOrder}
              last={key === lastKey}
              shown={beat === null || index < beat.shown}
              tone={toneFor(at, call)}
              fill={beat === null ? 'none' : fillFor(at)}
              fillDrawn={DRAWN_BEATS.includes(call)}
              fillLit={call === 'handoff' || call === 'answered'}
            />
          );
        })}
      </div>
      {beat !== null ? (
        <PlayCaption
          call={beat.call}
          first={providers[0].source.name}
          second={providers[1].source.name}
          onReplay={play.replay}
        />
      ) : null}
    </div>
  );
};
